import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Button,
  Tooltip,
  Divider,
  Badge,
} from '@fluentui/react-components';
import {
  HomeRegular,
  HomeFilled,
  PersonAddRegular,
  PersonAddFilled,
  PersonDeleteRegular,
  PersonDeleteFilled,
  PersonEditRegular,
  PersonEditFilled,
  LaptopRegular,
  LaptopFilled,
  CheckmarkCircleRegular,
  CheckmarkCircleFilled,
  PeopleTeamRegular,
  PeopleTeamFilled,
  PersonAccountsRegular,
  PersonAccountsFilled,
  SettingsRegular,
  SettingsFilled,
  HistoryRegular,
  HistoryFilled,
  ShieldCheckmarkRegular,
  ShieldCheckmarkFilled,
  KeyRegular,
  KeyFilled,
  bundleIcon,
} from '@fluentui/react-icons';
import { useAuth } from '../../hooks/useAuth';
import { ROUTES } from '../../config/constants';
import { apiClient } from '../../api/client';

const HomeIcon = bundleIcon(HomeFilled, HomeRegular);
const OnboardIcon = bundleIcon(PersonAddFilled, PersonAddRegular);
const OffboardIcon = bundleIcon(PersonDeleteFilled, PersonDeleteRegular);
const UpdateEmployeeIcon = bundleIcon(PersonEditFilled, PersonEditRegular);
const HardwareIcon = bundleIcon(LaptopFilled, LaptopRegular);
const ApprovalIcon = bundleIcon(CheckmarkCircleFilled, CheckmarkCircleRegular);
const TeamsIcon = bundleIcon(PeopleTeamFilled, PeopleTeamRegular);
const QuickCreateIcon = bundleIcon(PersonAccountsFilled, PersonAccountsRegular);
const AdminIcon = bundleIcon(SettingsFilled, SettingsRegular);
const HistoryIcon = bundleIcon(HistoryFilled, HistoryRegular);
const AuditIcon = bundleIcon(ShieldCheckmarkFilled, ShieldCheckmarkRegular);
const RegistryIcon = bundleIcon(KeyFilled, KeyRegular);

const useStyles = makeStyles({
  sidebar: {
    width: '220px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalXS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    marginTop: tokens.spacingVerticalM,
  },
  navButton: {
    justifyContent: 'flex-start',
    width: '100%',
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
  },
  activeButton: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    color: tokens.colorBrandForeground1,
  },
  badge: {
    marginLeft: 'auto',
  },
});

interface NavItem {
  path: string;
  label: string;
  icon: ReturnType<typeof bundleIcon>;
  adminOnly?: boolean;
  badge?: number;
}

export function Sidebar() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [pendingCount, setPendingCount] = useState<number>(0);

  // Fetch pending count for badge
  useEffect(() => {
    if (!isAdmin) return;

    apiClient
      .get<{ data: { pendingRequests: number } }>('/dashboard/stats')
      .then((response) => setPendingCount(response.data.pendingRequests))
      .catch(() => setPendingCount(0));
  }, [isAdmin]);

  const mainNavItems: NavItem[] = [
    { path: ROUTES.DASHBOARD, label: 'Dashboard', icon: HomeIcon },
    { path: ROUTES.ONBOARDING, label: 'Onboarding', icon: OnboardIcon },
    { path: ROUTES.OFFBOARDING, label: 'Offboarding', icon: OffboardIcon },
    { path: ROUTES.EMPLOYEE_UPDATE, label: 'Update Employee', icon: UpdateEmployeeIcon },
    { path: ROUTES.HARDWARE_REQUEST, label: 'Hardware Request', icon: HardwareIcon },
  ];

  const adminNavItems: NavItem[] = [
    { path: ROUTES.APPROVAL, label: 'Awaiting Approval', icon: ApprovalIcon, adminOnly: true, badge: pendingCount },
    { path: ROUTES.TEAMS, label: 'Create Teams Site', icon: TeamsIcon, adminOnly: true },
    { path: ROUTES.QUICK_CREATE, label: 'Quick Create User', icon: QuickCreateIcon, adminOnly: true },
    { path: ROUTES.AUDIT, label: 'Audit', icon: AuditIcon, adminOnly: true },
    { path: ROUTES.REMOTE_REGISTRY, label: 'Remote Registry', icon: RegistryIcon, adminOnly: true },
    { path: ROUTES.ADMIN, label: 'Admin Panel', icon: AdminIcon, adminOnly: true },
    { path: ROUTES.HISTORY, label: 'History', icon: HistoryIcon, adminOnly: true },
  ];

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  const renderNavItem = (item: NavItem) => {
    if (item.adminOnly && !isAdmin) return null;

    const Icon = item.icon;
    const active = isActive(item.path);

    return (
      <Tooltip content={item.label} relationship="label" positioning="after" key={item.path}>
        <Button
          appearance="subtle"
          className={`${styles.navButton} ${active ? styles.activeButton : ''}`}
          icon={<Icon filled={active} />}
          onClick={() => navigate(item.path)}
        >
          {item.label}
          {item.badge !== undefined && item.badge > 0 && (
            <Badge
              appearance="filled"
              color="danger"
              size="small"
              className={styles.badge}
            >
              {item.badge}
            </Badge>
          )}
        </Button>
      </Tooltip>
    );
  };

  return (
    <nav className={styles.sidebar}>
      <div className={styles.section}>
        {mainNavItems.map(renderNavItem)}
      </div>

      {isAdmin && (
        <>
          <Divider style={{ margin: `${tokens.spacingVerticalM} 0` }} />
          <div className={styles.sectionTitle}>Administration</div>
          <div className={styles.section}>
            {adminNavItems.map(renderNavItem)}
          </div>
        </>
      )}
    </nav>
  );
}
